
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import LoadingSpinner from '../../../components/base/LoadingSpinner';

export default function HeroSection() {
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (isLoading) {
    return (
      <section className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" color="blue" className="mb-4" />
          <p className="text-gray-300">Yükleniyor...</p>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="ana-sayfa" 
      className="min-h-screen relative overflow-hidden bg-gray-900"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/41870615e5320bf484b2d4139b946677.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
      
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium backdrop-blur-sm">
                <i className="ri-rocket-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Dijital Dönüşümde Öncü
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Geleceğin{' '}
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Dijital Çözümleri
                </span>
                {' '}Burada
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl">
                Modern web tasarımı, e-ticaret çözümleri ve mobil uygulamalarla 
                işletmenizi dijital dünyada zirveye taşıyoruz. Profesyonel ekibimizle 
                hayallerinizi gerçeğe dönüştürün.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('iletisim')}
                size="lg"
                className="group whitespace-nowrap"
                aria-label="Ücretsiz teklif almak için iletişim bölümüne git"
              >
                Ücretsiz Teklif Al
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center ml-2 transition-transform group-hover:translate-x-1"></i>
              </Button>
              
              <button
                onClick={() => scrollToSection('referanslar')}
                className="px-8 py-4 border-2 border-gray-600 text-white rounded-lg hover:border-blue-400 hover:text-blue-400 transition-all duration-300 font-semibold group whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Referanslarımızı görüntülemek için referanslar bölümüne git"
              >
                <i className="ri-eye-line w-5 h-5 flex items-center justify-center mr-2 inline-flex transition-transform group-hover:scale-110"></i>
                Referansları İncele
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-gray-700/50">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400 mb-1">50+</div>
                <div className="text-gray-300 text-sm">Tamamlanan Proje</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-1">98%</div>
                <div className="text-gray-300 text-sm">Müşteri Memnuniyeti</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500 mb-1">24/7</div>
                <div className="text-gray-300 text-sm">Teknik Destek</div>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative lg:block hidden">
            <div className="relative">
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-2xl">
                  <LoadingSpinner size="lg" color="blue" />
                </div>
              )}
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20web%20developer%20working%20on%20modern%20computer%20setup%2C%20multiple%20monitors%20displaying%20code%20and%20web%20designs%2C%20contemporary%20office%20environment%2C%20focused%20programmer%20at%20work%2C%20clean%20workspace%20with%20modern%20technology%2C%20software%20development%20scene%2C%20coding%20workspace%2C%20digital%20innovation%2C%20professional%20atmosphere%2C%20high-tech%20setup&width=600&height=700&seq=hero-dev-002&orientation=portrait"
                alt="Modern web geliştirme ve tasarım hizmetleri"
                className={`w-full h-auto rounded-2xl shadow-2xl object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={handleImageLoad}
                loading="lazy"
              />
              
              {/* Floating Elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl animate-bounce">
                <i className="ri-code-s-slash-line w-12 h-12 flex items-center justify-center text-white"></i>
              </div>
              
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-xl animate-pulse">
                <i className="ri-smartphone-line w-8 h-8 flex items-center justify-center text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection('ozellikler')}
          className="text-gray-400 hover:text-blue-400 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded p-2"
          aria-label="Özellikler bölümüne git"
        >
          <i className="ri-arrow-down-line w-6 h-6 flex items-center justify-center"></i>
        </button>
      </div>
    </section>
  );
}
